import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import HiringService, {
  Candidate, CandidateSource, SimilarResult,
} from '../../api/services/hiring.service';
import { UploadFileOutlined as ImportIcon } from '@mui/icons-material';
import { SOURCE_LABEL } from './hiringUI';

// Adding somebody to the pipeline.
//
// Four fields, and only the first is required. A lead is very often a
// first name and a phone number written down during a call, and a form
// that refuses that is a form people keep a spreadsheet to get around.
//
// The interesting part is the check that runs while you type: somebody
// already in the pipeline is a duplicate (two recruiters on the same
// lead), and somebody who already has an account here is a rehire. The old
// tracker carried a "Fired" status precisely because the second question
// had nowhere else to be asked.

const SOURCES: CandidateSource[] = [
  'indeed', 'referral', 'walk_in', 'facebook', 'craigslist', 'agency', 'rehire', 'other',
];

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '',
  source: 'indeed' as CandidateSource, sourceDetail: '', notes: '',
};

const AddCandidateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (candidate: Candidate) => void;
  /** Importing is another way to add people, so it is offered from here. */
  onImport?: () => void;
}> = ({ open, onClose, onCreated, onImport }) => {
  const [form, setForm] = useState(EMPTY);
  const [similar, setSimilar] = useState<SimilarResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setForm(EMPTY); setSimilar(null); setError(null); }
  }, [open]);

  const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // Debounced, and never blocking: the answer is information for the
  // person typing, not a gate. Plenty of legitimate candidates share a
  // phone with a household member.
  const check = useCallback(async (email: string, phone: string) => {
    if (!email.trim() && !phone.trim()) { setSimilar(null); return; }
    try {
      setSimilar(await HiringService.similar({ email: email.trim(), phone: phone.trim() }));
    } catch {
      setSimilar(null);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => check(form.email, form.phone), 500);
    return () => clearTimeout(timer);
  }, [form.email, form.phone, check]);

  const submit = async () => {
    if (!form.firstName.trim()) { setError('A candidate needs at least a first name.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const result = await HiringService.create({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        source: form.source,
        sourceDetail: form.sourceDetail.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      onCreated(result.candidate);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not add that candidate.');
    } finally {
      setSubmitting(false);
    }
  };

  const dupes = similar?.candidates ?? [];
  const rehires = similar?.accounts ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a candidate</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          A first name is enough to start. Everything else can be filled in as you learn it.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack gap={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="First name" value={form.firstName} onChange={set('firstName')}
              fullWidth required autoFocus
            />
            <TextField label="Last name" value={form.lastName} onChange={set('lastName')} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="Email" type="email" value={form.email} onChange={set('email')} fullWidth
              helperText="Needed before you can hire them — the invitation goes here."
            />
            <TextField label="Mobile number" value={form.phone} onChange={set('phone')} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              select label="Where they came from" value={form.source} onChange={set('source')}
              sx={{ minWidth: 180 }}
            >
              {SOURCES.map((s) => <MenuItem key={s} value={s}>{SOURCE_LABEL[s]}</MenuItem>)}
            </TextField>
            <TextField
              label="Who or which posting" value={form.sourceDetail} onChange={set('sourceDetail')}
              fullWidth placeholder="The person who referred them, the agency…"
            />
          </Stack>

          <TextField
            label="Notes" value={form.notes} onChange={set('notes')} fullWidth multiline minRows={2}
          />

          {dupes.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                Already in the pipeline
              </Typography>
              {dupes.map((c) => (
                <Typography key={c._id} variant="body2">
                  {c.firstName} {c.lastName} — {c.outcome === 'open' ? c.stage : c.outcome}
                </Typography>
              ))}
            </Alert>
          )}

          {rehires.length > 0 && (
            <Alert severity="info">
              <Typography variant="body2" fontWeight={700} gutterBottom>
                Somebody here already has this contact detail
              </Typography>
              {rehires.map((a) => (
                <Typography key={a.accountId} variant="body2">
                  {a.name} — {a.role}, {a.status}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary">
                Worth checking how that ended before you spend three weeks onboarding them again.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {/* Importing a spreadsheet is a way of ADDING people, not a
            separate decision to weigh before you have decided to add
            anybody. It was a second button in the panel competing with
            this one; it belongs behind this door. */}
        {onImport && (
          <Button
            startIcon={<ImportIcon />}
            onClick={onImport}
            sx={{ mr: 'auto' }}
          >
            Import a spreadsheet
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={submitting}>
          {submitting ? 'Adding…' : 'Add candidate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCandidateDialog;
